// src/modules/operaciones/inventarios/InventariosPrecios.js
//
// Módulo "Ajustar Precios" (SOLO LECTURA): lista los artículos con alertas de costo y existencia en Sysplus
// y permite filtrarlos, ordenarlos, paginarlos, ver su detalle por bodega y exportarlos a CSV.
//
// • Tipos de alerta: COSTO_CERO (saldo positivo con costo promedio 0) y NEGATIVO (existencia total negativa).
// • El backend calcula las alertas en segundo plano: mientras `meta.calculando` sea true, el componente
//   vuelve a consultar cada 5 segundos (polling) y muestra un banner de "Calculando…".
// • El filtro de estado se aplica en el backend (recarga datos); el de tipo y la búsqueda se aplican en el cliente.
// • El detalle de un artículo se pide al abrir el modal y se cancela (AbortController) si se cierra o se abre otro.

import React, { useState, useMemo, useCallback } from 'react'; // Importa React y los hooks usados
import { TEMAS_PLANOS, useTemaOscuro } from '../../../utils/tema'; // Paletas de clases (claro/oscuro) y hook del modo oscuro
import { fmt } from '../../../utils/format'; // Formatea números (separador de miles)
import { POR_PAGINA, MSG_VACIO, OPCIONES_ESTADO, OPCIONES_TIPO, COLUMNAS_NUMERICAS, colorSaldo } from './utils/preciosComun'; // Constantes y helpers compartidos
import { exportarCsv } from './utils/exportarCsvPrecios'; // Exportación a CSV
import usePreciosAlertas from './hooks/usePreciosAlertas'; // Carga con polling y detalle por artículo
import { BadgeTipo, Kpi, Spinner, ThOrden, TarjetaAlerta } from './components/ComponentesPrecios'; // Componentes visuales pequeños
import ModalDetalle from './components/ModalDetallePrecio'; // Modal de detalle por bodega

// useDeferredValue existe desde React 18; en React 17 se degrada a un passthrough sin romper
const useDeferred = React.useDeferredValue || ((v) => v);

/* ---------- Componente principal ---------- */
// Props: onBackToMenu = vuelve al menú interno de Inventarios
export default function InventariosPrecios({ onBackToMenu }) {
  /* === Estado === */
  const [busqueda, setBusqueda] = useState(''); // Texto del buscador (código o nombre)
  const [estado, setEstado] = useState('todos'); // Filtro de estado (se envía al backend)
  const [tipo, setTipo] = useState('todos'); // Filtro de tipo de alerta (en el cliente)
  const [pagina, setPagina] = useState(1); // Página actual de la tabla
  const [orden, setOrden] = useState({ campo: null, dir: 'asc' }); // Orden actual; campo null = sin ordenar
  const [recarga, setRecarga] = useState(0); // Contador que fuerza una nueva consulta al incrementarse
  const [temaOscuro, setTemaOscuro] = useTemaOscuro(); // Modo oscuro (persistido por el hook)

  // Carga principal con polling y detalle de artículo (ver hooks/usePreciosAlertas.js)
  const { articulos, meta, loading, lento, error, articuloDetalle, abrirDetalle, cerrarDetalle } = usePreciosAlertas({ estado, recarga });

  const busquedaDiferida = useDeferred(busqueda); // Búsqueda diferida: mantiene fluido el campo al escribir en listas grandes

  /* === Acciones de la tabla === */
  const recargar = () => { setPagina(1); setRecarga((n) => n + 1); }; // Vuelve a la página 1 y dispara una nueva consulta

  // Alterna el orden de una columna: ascendente → descendente → sin orden
  const alternarOrden = useCallback((campo) => {
    setOrden((prev) => {
      if (prev.campo !== campo) return { campo, dir: 'asc' };
      if (prev.dir === 'asc') return { campo, dir: 'desc' };
      return { campo: null, dir: 'asc' };
    });
    setPagina(1);
  }, []);

  /* === Datos derivados (filtrado, orden, KPIs y paginación) === */
  // Filtra por tipo de alerta y por texto (código o nombre, sin distinguir mayúsculas)
  const articulosFiltrados = useMemo(() => {
    const q = busquedaDiferida.trim().toLowerCase(); // Texto de búsqueda normalizado
    return articulos.filter((a) => {
      if (tipo === 'costo_cero' && a.TIPO_ALERTA !== 'COSTO_CERO') return false;
      if (tipo === 'negativos' && a.TIPO_ALERTA !== 'NEGATIVO') return false;
      return !q || (a.CODIGO || '').toLowerCase().includes(q) || (a.NOMBRE || '').toLowerCase().includes(q);
    });
  }, [articulos, busquedaDiferida, tipo]);

  // Ordena la lista filtrada: numérico para existencia/costo, texto natural (español) para el resto
  const articulosOrdenados = useMemo(() => {
    if (!orden.campo) return articulosFiltrados;
    const f = orden.dir === 'asc' ? 1 : -1; // Factor de dirección
    const { campo } = orden;
    const numerico = COLUMNAS_NUMERICAS.has(campo);
    return [...articulosFiltrados].sort((a, b) => (numerico
      ? (Number(a[campo]) || 0) - (Number(b[campo]) || 0)
      : String(a[campo] ?? '').localeCompare(String(b[campo] ?? ''), 'es', { numeric: true, sensitivity: 'base' })) * f);
  }, [articulosFiltrados, orden]);

  // Indicadores: usa los totales del backend (meta) y, si no vienen, los cuenta en el cliente
  const kpis = useMemo(() => ({
    total: articulos.length,
    costoCero: meta?.totalCostoCero ?? articulos.filter((a) => a.TIPO_ALERTA === 'COSTO_CERO').length,
    negativos: meta?.totalNegativos ?? articulos.filter((a) => a.TIPO_ALERTA === 'NEGATIVO').length,
  }), [articulos, meta]);

  // Paginación
  const totalPaginas = Math.max(1, Math.ceil(articulosOrdenados.length / POR_PAGINA)); // Total de páginas (mínimo 1)
  const paginaSegura = Math.min(pagina, totalPaginas); // Página corregida si los filtros redujeron el total
  const indiceInicial = (paginaSegura - 1) * POR_PAGINA; // Índice del primer registro de la página
  const articulosPagina = articulosOrdenados.slice(indiceInicial, indiceInicial + POR_PAGINA); // Filas visibles
  const desde = articulosOrdenados.length === 0 ? 0 : indiceInicial + 1; // Número del primer registro mostrado
  const hasta = Math.min(indiceInicial + POR_PAGINA, articulosOrdenados.length); // Número del último registro mostrado

  /* === Tema y clases reutilizables === */
  const tema = temaOscuro ? TEMAS_PLANOS.oscuro : TEMAS_PLANOS.claro; // Paleta activa

  const claseCardKpi = `${tema.bgCard} border ${tema.borderMain}`; // Estilo de las tarjetas KPI
  const botonPag = `px-3 h-8 flex items-center justify-center rounded-lg ${tema.bgBtn} ${tema.textMain} border ${tema.borderInput} disabled:opacity-40 hover:opacity-80 transition-colors shadow-sm text-xs font-bold cursor-pointer`; // Botones de paginación
  const botonAccion = `px-4 py-2.5 rounded-xl ${tema.bgBtn} ${tema.textMain} text-sm font-bold border ${tema.borderInput} hover:opacity-80 shadow-sm cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed`; // Botones de la cabecera
  const claseSelect = (ancho) => `${ancho} ${tema.bgInput} border ${tema.borderInput} ${tema.textInput} text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-purple-500 shadow-sm cursor-pointer`; // Estilo de los selects (recibe el ancho)
  const claseAviso = 'mb-4 px-4 py-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-600 text-sm font-bold'; // Estilo de los avisos ámbar
  // Configuración de los dos selects de filtro (tipo y estado) para renderizarlos con un solo map
  const filtrosSelect = [
    { valor: tipo, set: setTipo, opciones: OPCIONES_TIPO, ancho: 'lg:w-48', etiqueta: 'Filtrar por tipo de alerta' },
    { valor: estado, set: setEstado, opciones: OPCIONES_ESTADO, ancho: 'lg:w-52', etiqueta: 'Filtrar por estado del artículo' },
  ];

  return (
    <div className={`min-h-screen ${tema.bgApp} flex flex-col p-4 sm:p-8 font-sans transition-colors duration-300 relative`}>
      {/* Header */}
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8 gap-4">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-purple-600 flex items-center justify-center text-2xl shadow-lg shadow-purple-600/30">
            💲
          </div>
          <div>
            <h1 className={`text-2xl font-extrabold ${tema.textMain} tracking-tight`}>
              Ajustar <span className="text-purple-500">Precios</span>
            </h1>
            <p className={`${tema.textSub} text-sm mt-0.5 flex flex-wrap items-center gap-2`} aria-live="polite">
              <span>{loading ? 'Consultando…' : `${fmt(articulosOrdenados.length)} artículos con costo promedio 0 o saldo negativo`}</span>
            </p>
          </div>
        </div>
        {/* Acciones: recargar, exportar, tema y volver */}
        <div className="flex flex-wrap items-center gap-3">
          <button onClick={recargar} disabled={loading} className={botonAccion} title="Consultar de nuevo">
            🔄 Recargar
          </button>
          <button
            onClick={() => exportarCsv(articulosOrdenados)}
            disabled={loading || articulosOrdenados.length === 0}
            className={botonAccion}
            title="Descarga la lista filtrada y ordenada"
          >
            ⬇️ Exportar CSV
          </button>
          <button
            onClick={() => setTemaOscuro(!temaOscuro)}
            className={`p-2.5 rounded-xl ${tema.bgBtn} border ${tema.borderInput} ${tema.textMain} hover:opacity-80 shadow-sm cursor-pointer`}
            title={temaOscuro ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
          >
            {temaOscuro ? '☀️' : '🌙'}
          </button>
          <button onClick={onBackToMenu} className={botonAccion}>
            ← Volver al Menú
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Kpi titulo="Total alertas" valor={kpis.total} color={tema.textMain} loading={loading} claseCard={claseCardKpi} claseSub={tema.textSub} />
        <Kpi titulo="Costo 0" valor={kpis.costoCero} color="text-red-500" loading={loading} claseCard={claseCardKpi} claseSub={tema.textSub} />
        <Kpi titulo="Negativos" valor={kpis.negativos} color="text-amber-500" loading={loading} claseCard={claseCardKpi} claseSub={tema.textSub} />
      </div>

      {/* Búsqueda + filtros */}
      <div className="flex flex-col lg:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500">🔍</span>
          <input
            type="text"
            placeholder="Buscar por código o nombre..."
            value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setPagina(1); }}
            aria-label="Buscar por código o nombre"
            className={`w-full ${tema.bgInput} border ${tema.borderInput} ${tema.textInput} text-sm rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:border-purple-500 shadow-sm`}
          />
        </div>
        {filtrosSelect.map((f) => (
          <select
            key={f.etiqueta}
            value={f.valor}
            onChange={(e) => { f.set(e.target.value); setPagina(1); }}
            aria-label={f.etiqueta}
            className={claseSelect(f.ancho)}
          >
            {f.opciones.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        ))}
      </div>

      {/* Leyenda */}
      <div className={`flex flex-wrap items-center gap-3 mb-4 text-xs ${tema.textSub}`}>
        <span className="font-bold uppercase tracking-wider">Leyenda:</span>
        <span className="flex items-center gap-1.5"><BadgeTipo tipoAlerta="COSTO_CERO" claseSub={tema.textSub} /> saldo positivo con costo promedio 0</span>
        <span className="flex items-center gap-1.5"><BadgeTipo tipoAlerta="NEGATIVO" claseSub={tema.textSub} /> saldo negativo</span>
      </div>

      {/* Error de carga con botón para reintentar */}
      {error && (
        <div role="alert" aria-live="assertive" className="mb-4 px-4 py-3 rounded-xl border border-red-500/40 bg-red-500/10 text-red-500 text-sm font-bold flex items-center justify-between gap-3">
          <span>Error: {error}</span>
          <button onClick={recargar} className="px-3 py-1.5 rounded-lg text-xs font-bold border border-red-500/40 hover:bg-red-500/10 cursor-pointer">Reintentar</button>
        </div>
      )}

      {/* BANNER DE CALCULANDO */}
      {!loading && meta?.calculando && (
        <div role="status" className="mb-4 px-4 py-3 rounded-xl border border-blue-500/40 bg-blue-500/10 text-blue-600 text-sm font-bold flex items-center gap-3">
          <Spinner />
          Calculando existencias y costos por primera vez... Esto puede tardar hasta 1 minuto.
        </div>
      )}

      {/* Aviso: el backend truncó la lista al límite */}
      {!loading && meta?.truncado && (
        <div role="status" className={claseAviso}>
          La lista se limitó a {fmt(meta.limite)} artículos y puede estar incompleta.
          Los negativos y los de mayor saldo aparecen primero. Acote con el filtro de estado para revisar el resto.
        </div>
      )}

      {/* Aviso de carga lenta */}
      {loading && lento && (
        <div className={`mb-4 px-4 py-3 rounded-xl border ${tema.borderInput} ${tema.bgCard} ${tema.textSub} text-sm`}>
          Cargando… si tarda demasiado revisa la conexión a Sysplus.
        </div>
      )}

      {/* Tabla (escritorio) */}
      <div className={`hidden md:flex ${tema.bgCard} rounded-2xl border ${tema.borderMain} shadow-xl overflow-hidden flex-1 flex-col`}>
        <div className="overflow-x-auto">
          <table className={`w-full text-left text-sm ${tema.textSub} whitespace-nowrap`} aria-busy={loading}>
            <thead className={`${tema.bgHeader} ${tema.textSub} text-xs uppercase tracking-wider border-b ${tema.borderMain}`}>
              <tr>
                <ThOrden campo="CODIGO" label="Código" align="" orden={orden} onOrden={alternarOrden} />
                <ThOrden campo="NOMBRE" label="Nombre del Artículo" align="" orden={orden} onOrden={alternarOrden} />
                <th className="px-6 py-4 font-bold text-center">Tipo</th>
                <ThOrden campo="EXISTENCIA_TOTAL" label="Existencia Total" align="text-center" orden={orden} onOrden={alternarOrden} />
                <th className="px-6 py-4 font-bold text-right">Precio Actual</th>
                <ThOrden campo="COSTO_PROMEDIO" label="Costo Promedio" align="text-right" orden={orden} onOrden={alternarOrden} />
                <th className="px-6 py-4 font-bold text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className={`divide-y ${tema.borderMain}`}>
              {loading ? (
                // Filas esqueleto mientras carga
                [0, 1, 2].map((i) => (
                  <tr key={i} className="animate-pulse">
                    <td colSpan="7" className="px-6 py-4"><div className={`h-6 rounded-lg ${tema.bgSkeleton}`} /></td>
                  </tr>
                ))
              ) : articulosPagina.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                    {meta?.calculando ? 'Calculando datos, por favor espere...' : MSG_VACIO}
                  </td>
                </tr>
              ) : (
                articulosPagina.map((a) => (
                  <tr key={a.CODIGO} className={`${tema.bgHover} transition-colors`}>
                    <td className="px-6 py-4 font-mono text-purple-500 font-bold">{a.CODIGO}</td>
                    <td className={`px-6 py-4 max-w-[400px] truncate ${tema.textMain}`} title={a.NOMBRE}>{a.NOMBRE}</td>
                    <td className="px-6 py-4 text-center"><BadgeTipo tipoAlerta={a.TIPO_ALERTA} claseSub={tema.textSub} /></td>
                    <td className={`px-6 py-4 text-center font-mono font-bold text-base ${colorSaldo(a.EXISTENCIA_TOTAL)}`}>{fmt(a.EXISTENCIA_TOTAL)}</td>
                    <td className="px-6 py-4 text-right font-mono text-red-500">$ {fmt(a.PRECIO_ACTUAL)}</td>
                    <td className="px-6 py-4 text-right font-mono text-red-500">$ {fmt(a.COSTO_PROMEDIO)}</td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => { e.currentTarget.blur(); abrirDetalle(a); }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${tema.borderInput} ${tema.bgBtn} ${tema.textMain} hover:opacity-80 transition-colors cursor-pointer`}
                      >
                        Detalles
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tarjetas (móvil) */}
      <div className="md:hidden flex flex-col gap-3" aria-busy={loading}>
        {loading ? (
          [0, 1, 2].map((i) => <div key={i} className={`h-32 rounded-2xl animate-pulse ${tema.bgSkeleton}`} />)
        ) : articulosPagina.length === 0 ? (
          <p className="py-10 text-center text-gray-500 text-sm">{meta?.calculando ? 'Calculando datos...' : MSG_VACIO}</p>
        ) : (
          articulosPagina.map((a) => <TarjetaAlerta key={a.CODIGO} a={a} tema={tema} onDetalle={abrirDetalle} />)
        )}
      </div>

      {/* Paginación */}
      <div className="flex flex-col sm:flex-row justify-between items-center mt-4 gap-3">
        <p className={`${tema.textSub} text-xs`} aria-live="polite">
          Mostrando {fmt(desde)}–{fmt(hasta)} de {fmt(articulosOrdenados.length)} registros
        </p>
        <div className="flex items-center gap-2">
          <button onClick={() => setPagina(Math.max(1, paginaSegura - 1))} disabled={paginaSegura === 1 || loading} className={botonPag}>
            ‹ Anterior
          </button>
          <span className={`${tema.textSub} text-xs px-2`}>Página {paginaSegura} de {totalPaginas}</span>
          <button onClick={() => setPagina(Math.min(totalPaginas, paginaSegura + 1))} disabled={paginaSegura === totalPaginas || loading} className={botonPag}>
            Siguiente ›
          </button>
        </div>
      </div>

      {/* Modal de detalle (solo si hay un artículo seleccionado) */}
      {articuloDetalle && <ModalDetalle articulo={articuloDetalle} tema={tema} onClose={cerrarDetalle} />}
    </div>
  );
}